import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { alertsAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  LayoutGrid,
  Megaphone,
  BarChart3,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Shield,
  Mail,
  Plug,
  Link2,
  Building2,
  Settings2,
  Upload,
  Bell,
  Calculator,
  Check,
  AlertCircle,
  TrendingDown,
  DollarSign,
  Target,
  FileText,
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, advertiser, canManageUsers, canManageAdmins, isSuperAdmin, isAdvertiser } = useAuth();
  
  // Simplified navigation for advertiser and user roles
  const isAdvertiserUser = isAdvertiser();
  const isAdvertiserRole = user?.role === 'advertiser';
  const isUserRole = user?.role === 'user';
  const isRestrictedRole = isAdvertiserRole || isUserRole;
  
  // Determine logo to show
  const showAdvertiserLogo = isRestrictedRole && advertiser?.logo_url;

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutGrid, show: true },
    { path: '/campaigns', label: 'Platform Campaigns', icon: Megaphone, show: !isRestrictedRole },
    // Campaigns for advertiser/user roles
    { path: '/my-campaigns', label: 'Campaigns', icon: Megaphone, show: isRestrictedRole },
    { path: '/campaign-groups', label: 'Campaign Groups', icon: Link2, show: true },
    { path: '/reports', label: 'Reports', icon: BarChart3, show: true },
    { path: '/performance-report', label: 'Performance Report', icon: FileText, show: true },
    // Advertiser self-service portal (show for both advertiser and user roles)
    { path: `/my-account`, label: 'My Account', icon: Building2, show: isRestrictedRole },
    // Admin-only advertiser management
    { path: '/advertisers', label: 'Advertisers', icon: Building2, show: user?.role === 'super_admin' || user?.role === 'admin' },
    { path: '/integrations', label: 'Integrations', icon: Plug, show: isSuperAdmin() },
    { path: '/report-schedules', label: 'Email Reports', icon: Mail, show: isSuperAdmin() },
    { path: '/alerts', label: 'Alerts', icon: Bell, show: user?.role === 'super_admin' || user?.role === 'admin' },
    { path: '/custom-metrics', label: 'Custom Metrics', icon: Calculator, show: user?.role === 'super_admin' || user?.role === 'admin' },
    { path: '/data-import', label: 'Data Import', icon: Upload, show: user?.role === 'super_admin' || user?.role === 'admin' },
    { path: '/custom-fields', label: 'Custom Fields', icon: Settings2, show: isSuperAdmin() },
    { path: '/users', label: 'Users', icon: Users, show: canManageUsers() },
    { path: '/admins', label: 'Admins', icon: Shield, show: canManageAdmins() },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-60 bg-white border-r border-slate-200 transition-transform duration-200 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            {showAdvertiserLogo ? (
              <>
                <img 
                  src={advertiser.logo_url} 
                  alt={advertiser.name || 'Company Logo'} 
                  className="h-8 w-8 object-contain rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden h-8 w-8 bg-slate-200 rounded items-center justify-center">
                  <Building2 className="h-4 w-4 text-slate-500" />
                </div>
                <span className="font-heading font-bold text-base text-slate-900 truncate">
                  {advertiser.name || 'My Company'}
                </span>
              </>
            ) : (
              <span className="font-heading font-bold text-lg text-slate-900">Report PRO</span>
            )}
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-1 hover:bg-slate-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.filter(item => item.show).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={cn(
                  'sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors',
                  isActive
                    ? 'active'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const [notificationsRes, countRes] = await Promise.all([
        alertsAPI.getNotifications(false), // Get all notifications
        alertsAPI.getUnreadCount(),
      ]);
      setNotifications(notificationsRes.data.slice(0, 5)); // Show last 5
      setUnreadCount(countRes.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await alertsAPI.markRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await alertsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'spend_threshold':
        return <DollarSign className="w-4 h-4 text-amber-500" />;
      case 'ctr_drop':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'conversions':
        return <Target className="w-4 h-4 text-green-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative p-2"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
              data-testid="notification-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              data-testid="mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors",
                  !notification.is_read && "bg-blue-50/50"
                )}
                onClick={() => {
                  if (!notification.is_read) handleMarkRead(notification.id);
                }}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getAlertIcon(notification.alert_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm text-slate-900 line-clamp-2",
                      !notification.is_read && "font-medium"
                    )}>
                      {notification.message}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="px-4 py-2 border-t border-slate-200">
          <button
            onClick={() => {
              setOpen(false);
              navigate('/alerts');
            }}
            className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
            data-testid="view-all-notifications"
          >
            View all alerts
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="h-full px-4 lg:px-8 flex items-center justify-between">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 hover:bg-slate-100 rounded-md"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Page title area - can be customized per page */}
        <div className="hidden lg:block" />

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Notification Bell */}
          <NotificationBell />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-600" />
                </div>
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:pl-60">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
